import { Modal, ModalHeader, ModalBody, Spinner } from 'reactstrap';
import { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import PerformanceFeedback from '../../../../services/PerformanceFeedback';
import PriorDiscussionsTable from './PriorDiscussionsTable';

interface PriorDiscussion {
  date_of_incident: string;
  type: string;
  policy_violated: string;
}

interface PriorDiscussionsModalProps {
  isOpen: boolean;
  toggle: () => void;
  userId: string | null;
  userName?: string;
}

const PriorDiscussionsModal = ({ isOpen, toggle, userId, userName }: PriorDiscussionsModalProps) => {
  const [discussions, setDiscussions] = useState<PriorDiscussion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { token }: any = useContext(AuthContext);

  const loadDiscussions = useCallback(async () => {
    if (!userId || !token) {
      return;
    }

    try {
      setIsLoading(true);
      const service = PerformanceFeedback(token);
      const response = await service.getUserFeedbacks(userId);

      const discussionData = response?.data || [];
      const policyLabel = (item: any) => {
        if (item.policy_violated) {
          return item.policy_violated;
        }
        if (item.policyViolation?.name) {
          return item.policyViolation.name;
        }
        if (typeof item.policyViolation === 'string') {
          return item.policyViolation;
        }
        if (Array.isArray(item.policyViolations) && item.policyViolations.length > 0) {
          return item.policyViolations
            .map((pv: any) => (typeof pv === 'string' ? pv : pv?.name || ''))
            .filter(Boolean)
            .join(', ');
        }
        return '';
      };
      const formattedDiscussions = discussionData.map((item: any) => ({
        date_of_incident: item.date_of_incident ?? item.dateOfIncident ?? item.date ?? '',
        type: item.type,
        policy_violated: policyLabel(item),
      }));

      setDiscussions(formattedDiscussions);
    } catch (error: any) {
      console.error('Error loading prior discussions:', error);
      toast.error(error?.response?.data?.message || 'Error loading prior discussions');
      setDiscussions([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    if (isOpen && userId) {
      loadDiscussions();
    }
  }, [isOpen, userId, loadDiscussions]);

  const handleClose = () => {
    setDiscussions([]);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>Prior Discussions {userName && `- ${userName}`}</ModalHeader>
      <ModalBody>
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <PriorDiscussionsTable discussions={discussions} />
        )}
      </ModalBody>
    </Modal>
  );
};

export default PriorDiscussionsModal;
